/** Ed25519 identity keys, FACE addresses and human-transcribable recovery keys. */

import { ed25519, x25519 } from '@noble/curves/ed25519';
import { ml_dsa44 } from '@noble/post-quantum/ml-dsa.js';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { b32decode, b32encode, concat, equalBytes, fromHex, randomBytes, toHex, utf8, type Hex } from './bytes.ts';
import { tag } from './hash.ts';

export const ADDRESS_PREFIX = 'face1';

export interface KeyPair {
  /** 32-byte ed25519 seed. The only secret that matters. */
  secretKey: Uint8Array;
  publicKey: Uint8Array;
  publicKeyHex: Hex;
  address: string;
}

export function generateKeyPair(): KeyPair {
  return keyPairFromSeed(randomBytes(32));
}

export function keyPairFromSeed(seed: Uint8Array): KeyPair {
  if (seed.length !== 32) throw new Error('seed must be 32 bytes');
  const publicKey = ed25519.getPublicKey(seed);
  return {
    secretKey: seed,
    publicKey,
    publicKeyHex: toHex(publicKey),
    address: addressFromPublicKey(publicKey),
  };
}

export function sign(secretKey: Uint8Array, message: Uint8Array): Uint8Array {
  return ed25519.sign(message, secretKey);
}

export function verify(publicKey: Uint8Array | Hex, message: Uint8Array, signature: Uint8Array | Hex): boolean {
  try {
    const pk = typeof publicKey === 'string' ? fromHex(publicKey) : publicKey;
    const sg = typeof signature === 'string' ? fromHex(signature) : signature;
    if (pk.length !== 32 || sg.length !== 64) return false;
    return ed25519.verify(sg, message, pk);
  } catch {
    return false;
  }
}

/* ----------------------------- addresses ---------------------------- */

/**
 * address = "face1" + base32( sha256-tag(pubkey)[0..20] || checksum[0..4] )
 * A 20-byte account id gives 160 bits of collision resistance; the 4-byte
 * checksum catches typos before a payment leaves the wallet.
 */
export function addressFromPublicKey(publicKey: Uint8Array): string {
  const payload = tag('face/addr/v1', publicKey).slice(0, 20);
  const check = tag('face/addr/checksum/v1', payload).slice(0, 4);
  return ADDRESS_PREFIX + b32encode(concat(payload, check));
}

export function isValidAddress(address: string): boolean {
  if (typeof address !== 'string') return false;
  const s = address.trim();
  if (!s.toLowerCase().startsWith(ADDRESS_PREFIX)) return false;
  const body = s.slice(ADDRESS_PREFIX.length).toUpperCase();
  try {
    const raw = b32decode(body);
    if (raw.length !== 24) return false;
    const payload = raw.slice(0, 20);
    const check = raw.slice(20, 24);
    if (!equalBytes(check, tag('face/addr/checksum/v1', payload).slice(0, 4))) return false;
    // 24 bytes is 192 bits, which does not divide into 5, so the final
    // base32 character carries three padding bits that decoding throws
    // away. Without this re-encode, a typo landing in those bits would
    // sail through the checksum and money would go to a mistyped address.
    return b32encode(raw) === body;
  } catch {
    return false;
  }
}

export function normalizeAddress(address: string): string {
  const s = address.trim().replace(/\s+/g, '');
  return ADDRESS_PREFIX + s.slice(ADDRESS_PREFIX.length).toUpperCase();
}

export function shortAddress(address: string, n = 6): string {
  const body = address.slice(ADDRESS_PREFIX.length);
  return ADDRESS_PREFIX + body.slice(0, n) + '…' + body.slice(-n);
}

/* --------------------------- recovery keys -------------------------- */

/**
 * 32-byte seed + 2-byte checksum in Crockford base32, grouped in fives.
 * Case-insensitive, no ambiguous glyphs, no wordlist to ship or translate.
 */
export function encodeRecoveryKey(seed: Uint8Array): string {
  if (seed.length !== 32) throw new Error('seed must be 32 bytes');
  const check = tag('face/recovery/v1', seed).slice(0, 2);
  const s = b32encode(concat(seed, check));
  return (s.match(/.{1,5}/g) || []).join('-');
}

export function decodeRecoveryKey(text: string): Uint8Array {
  const raw = b32decode(text);
  if (raw.length < 34) throw new Error('recovery key is too short');
  const seed = raw.slice(0, 32);
  const check = raw.slice(32, 34);
  if (!equalBytes(check, tag('face/recovery/v1', seed).slice(0, 2))) {
    throw new Error('recovery key checksum does not match - check for a mistyped character');
  }
  return seed;
}

/* ------------------------ post-quantum keys ------------------------- */

/**
 * Every account also holds an ML-DSA-44 key, derived from the same seed.
 *
 * Ed25519 is not merely weakened by a quantum computer, it is undone by
 * it: Shor's algorithm recovers the private key from the public one, and
 * this ledger publishes the public key of every account the moment it
 * claims. A currency meant to outlast a government cannot rest on that.
 *
 * The two signatures are required together, not either-or. An adversary
 * must break both, so the hybrid is at least as strong as the stronger of
 * them - which is the only composition worth having while lattice
 * cryptography is still young enough to surprise us.
 *
 * Cost: 1312 bytes of public key, registered once with the claim, and
 * 2420 bytes of signature per vertex. Roughly nine milliseconds to sign
 * and one and a half to verify.
 */
export function pqKeyPairFromSeed(seed: Uint8Array): { secretKey: Uint8Array; publicKey: Uint8Array } {
  const material = hkdf(sha256, seed, utf8('face/pq/v1'), utf8('ml-dsa-44'), 32);
  return ml_dsa44.keygen(material);
}

export function pqSign(seed: Uint8Array, message: Uint8Array): Uint8Array {
  return ml_dsa44.sign(message, pqKeyPairFromSeed(seed).secretKey);
}

export function pqVerify(publicKey: Uint8Array | Hex, message: Uint8Array, signature: Uint8Array | Hex): boolean {
  try {
    const pk = typeof publicKey === 'string' ? fromHex(publicKey) : publicKey;
    const sg = typeof signature === 'string' ? fromHex(signature) : signature;
    if (pk.length !== 1312 || sg.length !== 2420) return false;
    return ml_dsa44.verify(sg, message, pk);
  } catch {
    return false;
  }
}

/* --------------------- face-bound wallet keys ----------------------- */

/**
 * The wallet key for a person, from their face and nothing else.
 *
 * READ THIS BEFORE RELYING ON IT. The secret this consumes is recovered
 * from a public helper published on the ledger (see fuzzy.ts), masked with
 * bits derived from the face embedding. So anyone who can reproduce that
 * embedding - which an ordinary photograph is usually enough for - can
 * recover the same secret offline and derive this same key. The liveness
 * check cannot prevent it: liveness runs in the wallet, and someone
 * stealing a wallet writes their own client.
 *
 * In other words, under this design a good photograph of a person is
 * equivalent to their private key. That is a deliberate product decision
 * taken in exchange for a wallet that opens with nothing but a face on any
 * device in the world, and it is stated here, in the UI, and in
 * WHITEPAPER.md section 4.2 rather than left for someone to discover.
 *
 * There is no passphrase in this derivation, which is the whole point: a
 * key that needed one would not open from a face alone. The passphrase the
 * wallet still asks for at sign-up locks the *local* copy on that device
 * and provides a way in when a camera is unavailable; it does not protect
 * the wallet from the attack above, and the wallet says so.
 *
 * A plain KDF rather than a slow one is correct here: the input is 128
 * bits of randomness, so there is nothing to brute-force, and spending
 * seconds on it would buy nothing but delay.
 */
export function deriveWalletSeed(faceSecret: Uint8Array, deviceSecret?: Uint8Array): Uint8Array {
  if (faceSecret.length !== 16 && faceSecret.length !== 32) {
    throw new Error('face secret must be 16 or 32 bytes');
  }
  const salt = tag('face/wallet-salt/v1', faceSecret);
  if (!deviceSecret) {
    // No PRF on this platform. The face alone derives the key, and a
    // photograph derives it too - which is survivable only because the
    // key cannot spend without the authenticator's approval.
    return hkdf(sha256, faceSecret, salt, utf8('face/wallet/v1'), 32);
  }
  if (deviceSecret.length < 16) throw new Error('device secret is too short');
  // With PRF the two halves are combined before the key exists at all, so
  // an image yields nothing to work with rather than a key that happens to
  // be useless. Defence in depth: the assertion requirement already stops
  // the spend, and this stops the derivation.
  return hkdf(sha256, concat(faceSecret, deviceSecret), salt, utf8('face/wallet/v2'), 32);
}

/**
 * The fixed salt the PRF is evaluated at.
 *
 * Constant on purpose: the same credential must return the same bytes on
 * every device the passkey has synced to, or the wallet would not open
 * where the person expects it to.
 */
export const WALLET_PRF_SALT = tag('face/wallet-prf/v1');

/** Shortest passphrase the wallet will accept for the local device lock. */
export const MIN_PASSPHRASE_LENGTH = 10;

export interface PassphraseVerdict {
  ok: boolean;
  /** 0-4, for a strength meter. */
  score: number;
  message: string;
}

/**
 * Passphrase guidance for the device lock. Length dominates because it is
 * the only thing that reliably resists guessing, and because it is the
 * only advice a person can act on without a chart.
 */
export function assessPassphrase(passphrase: string): PassphraseVerdict {
  const s = passphrase ?? '';
  if (s.length < MIN_PASSPHRASE_LENGTH) {
    return { ok: false, score: 0, message: `At least ${MIN_PASSPHRASE_LENGTH} characters - you have ${s.length}.` };
  }
  if (/^(.)\1+$/.test(s)) return { ok: false, score: 0, message: 'One repeated character is not a passphrase.' };
  if (/^(0123456789|1234567890|abcdefghij|qwertyuiop|password|passphrase)/i.test(s)) {
    return { ok: false, score: 0, message: 'That is one of the first things anyone would try.' };
  }

  const words = s.trim().split(/\s+/).filter(Boolean).length;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(s)).length;
  let score = 1;
  if (s.length >= 14 || words >= 3) score = 2;
  if (s.length >= 20 || (words >= 4 && s.length >= 16)) score = 3;
  if (s.length >= 28 || (words >= 5 && s.length >= 24)) score = 4;
  if (classes >= 3 && score < 4) score++;

  const message = score >= 3
    ? 'Strong. Write it somewhere safe - it cannot be reset.'
    : score === 2
      ? 'Reasonable. A few unrelated words would be stronger.'
      : 'Weak. Longer beats clever - try four unrelated words.';
  return { ok: true, score, message };
}

/* ------------------------- derived subkeys -------------------------- */

/** Deterministic X25519 keypair for encrypted peer links, from the same seed. */
export function linkKeyPair(seed: Uint8Array): { secretKey: Uint8Array; publicKey: Uint8Array } {
  const sk = hkdf(sha256, seed, utf8('face/link/v1'), utf8('x25519'), 32);
  return { secretKey: sk, publicKey: x25519.getPublicKey(sk) };
}

/** Deterministic AES-256 key for at-rest ledger encryption, from the same seed. */
export function vaultKey(seed: Uint8Array, scope: string): Uint8Array {
  return hkdf(sha256, seed, utf8('face/vault/v1'), utf8(scope), 32);
}
